import { cva, type VariantProps } from "class-variance-authority";
import { type ComponentProps, memo, useMemo } from "react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type JsonTokenType = "key" | "str" | "num" | "true" | "false" | "null";

type JsonToken = { type: JsonTokenType; text: string; offset: number } | string;

const JSON_TOKEN_RE =
  /("(?:[^"\\]|\\.)*")\s*(?=:)|("(?:[^"\\]|\\.)*")|\b(true)\b|\b(false)\b|\b(null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

function tokenize(code: string): JsonToken[] {
  const tokens: JsonToken[] = [];
  let lastIndex = 0;

  for (const match of code.matchAll(JSON_TOKEN_RE)) {
    const idx = match.index;
    if (idx > lastIndex) {
      tokens.push(code.slice(lastIndex, idx));
    }

    const [full, key, str, kwTrue, kwFalse, kwNull, num] = match;
    if (key) {
      tokens.push({ type: "key", text: key, offset: idx });
    } else if (str) {
      tokens.push({ type: "str", text: str, offset: idx });
    } else if (kwTrue) {
      tokens.push({ type: "true", text: kwTrue, offset: idx });
    } else if (kwFalse) {
      tokens.push({ type: "false", text: kwFalse, offset: idx });
    } else if (kwNull) {
      tokens.push({ type: "null", text: kwNull, offset: idx });
    } else if (num) {
      tokens.push({ type: "num", text: num, offset: idx });
    }

    lastIndex = idx + full.length;
  }

  if (lastIndex < code.length) {
    tokens.push(code.slice(lastIndex));
  }

  return tokens;
}

// ---------------------------------------------------------------------------
// Theme variants
// ---------------------------------------------------------------------------
// Each variant sets CSS custom properties that token spans read via var().
// Consumers can override individual vars through `className`:
//
//   <JsonHighlight className="[--jh-key:#2563eb]" />

const jsonHighlightVariants = cva(
  "w-full overflow-x-auto text-[13px] text-foreground/90",
  {
    variants: {
      theme: {
        default: [
          "[--jh-key:#8a6c14] dark:[--jh-key:#b8a965]",
          "[--jh-str:#a8453a] dark:[--jh-str:#c98a7d]",
          "[--jh-num:#0f766e] dark:[--jh-num:#4c9a91]",
          "[--jh-true:#2f6e4e] dark:[--jh-true:#4d9375]",
          "[--jh-false:#a8453a] dark:[--jh-false:#c98a7d]",
          "[--jh-null:#71717a] dark:[--jh-null:#a1a1aa]",
        ],
        branded: [
          "[--jh-key:var(--chart-2)]",
          "[--jh-str:var(--chart-1)]",
          "[--jh-num:var(--warning)]",
          "[--jh-true:var(--success)]",
          "[--jh-false:var(--destructive)]",
          "[--jh-null:var(--color-muted-foreground)]",
        ],
        monochrome: [
          "[--jh-key:var(--color-foreground)]",
          "[--jh-str:var(--color-muted-foreground)]",
          "[--jh-num:var(--color-muted-foreground)]",
          "[--jh-true:var(--color-muted-foreground)]",
          "[--jh-false:var(--color-muted-foreground)]",
          "[--jh-null:var(--color-muted-foreground)]",
        ],
      },
    },
    defaultVariants: { theme: "default" },
  }
);

const TOKEN_CLASSES: Record<JsonTokenType, string> = {
  key: "text-[var(--jh-key)]",
  str: "text-[var(--jh-str)]",
  num: "text-[var(--jh-num)]",
  true: "text-[var(--jh-true)]",
  false: "text-[var(--jh-false)]",
  null: "text-[var(--jh-null)]",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type JsonHighlightProps = {
  code: string;
} & VariantProps<typeof jsonHighlightVariants> &
  ComponentProps<"div">;

/**
 * Lightweight, synchronous JSON syntax highlighter.
 *
 * Uses a single-pass regex tokenizer — **O(n)** over the input string — to
 * colour keys, strings, numbers, and keywords (true / false / null).
 *
 * ## Themes
 *
 * Use the `theme` prop to pick a colour preset:
 *
 * - `"default"` — amber keys, red strings, teal numbers, green true, red false, gray null.
 * - `"branded"` — uses design system tokens (chart-2, chart-1, warning, success, destructive).
 * - `"monochrome"` — foreground keys, muted values (for subdued contexts).
 *
 * ## Customising individual colours
 *
 * Token colours are CSS custom properties. Override any via `className`:
 *
 * | Variable     | Token        |
 * | ------------ | ------------ |
 * | `--jh-key`   | Object keys  |
 * | `--jh-str`   | Strings      |
 * | `--jh-num`   | Numbers      |
 * | `--jh-true`  | `true`       |
 * | `--jh-false` | `false`      |
 * | `--jh-null`  | `null`       |
 *
 * ```tsx
 * <JsonHighlight
 *   code={json}
 *   className="[--jh-str:#2563eb] dark:[--jh-str:#93c5fd]"
 * />
 * ```
 *
 * ## When to use this over `CodeBlockCode`
 *
 * - The content is **always JSON** (no multi-language support needed).
 * - The component appears in a **hot path** where Shiki's WASM bundle
 *   (~695 KB gzipped) and async initialisation would cause visible lag.
 * - You need **synchronous first-paint** (no loading flash).
 *
 * ## When to prefer `CodeBlockCode` (Shiki)
 *
 * - The language is not JSON, or can vary at runtime.
 * - You need advanced features like line numbers or line highlighting.
 * - The code block renders once in a non-latency-critical context.
 */
const JsonHighlight = memo(function JsonHighlight({
  code,
  theme,
  className,
  ...props
}: JsonHighlightProps) {
  const tokens = useMemo(() => tokenize(code), [code]);

  return (
    <div
      className={cn(jsonHighlightVariants({ theme }), className)}
      data-slot="json-highlight"
      {...props}
    >
      <pre className="wrap-break-word max-w-full whitespace-pre-wrap p-3 font-mono leading-relaxed">
        <code>
          {tokens.map((token) => {
            if (typeof token === "string") {
              return token;
            }
            return (
              <span className={TOKEN_CLASSES[token.type]} key={token.offset}>
                {token.text}
              </span>
            );
          })}
        </code>
      </pre>
    </div>
  );
});

export type { JsonHighlightProps };
export { JsonHighlight, jsonHighlightVariants };
